/**
 * Keyboard -> Action mapping, with DAS/ARR for horizontal movement.
 *
 * Repeat timing lives here, not in the core: the core only ever sees discrete
 * `MoveLeft` / `MoveRight` actions. Like the core, this module takes time as an
 * argument (`update(dtMs)`) rather than reading a clock.
 */

import { Action, TUNING } from '../core/game.ts';

export type Dispatch = (action: Action) => void;

/** Physical key -> action for keys that fire once per press. */
const KEY_ACTIONS: Record<string, Action> = {
  Space: Action.HardDrop,
  KeyZ: Action.RotateCCW,
  KeyX: Action.RotateCW,
  ArrowUp: Action.RotateCW,
  KeyA: Action.Rotate180,
  KeyC: Action.Hold,
  ShiftLeft: Action.Hold,
  ShiftRight: Action.Hold,
  Escape: Action.Pause,
  KeyP: Action.Pause,
  KeyR: Action.Restart,
  Enter: Action.Start,
};

type Direction = -1 | 1;

export class KeyboardInput {
  private readonly dispatch: Dispatch;
  private readonly target: HTMLElement | Window;

  /** Directions currently held, most recent last. */
  private held: Direction[] = [];
  private dasChargeMs = 0;
  private arrAccMs = 0;
  private softDropHeld = false;
  private attached = false;

  private readonly onKeyDown = (event: KeyboardEvent): void => this.keyDown(event);
  private readonly onKeyUp = (event: KeyboardEvent): void => this.keyUp(event);
  private readonly onBlur = (): void => this.releaseAll();

  constructor(dispatch: Dispatch, target: HTMLElement | Window = window) {
    this.dispatch = dispatch;
    this.target = target;
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.target.addEventListener('keydown', this.onKeyDown as EventListener);
    this.target.addEventListener('keyup', this.onKeyUp as EventListener);
    window.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.target.removeEventListener('keydown', this.onKeyDown as EventListener);
    this.target.removeEventListener('keyup', this.onKeyUp as EventListener);
    window.removeEventListener('blur', this.onBlur);
    this.releaseAll();
  }

  /** Advance DAS/ARR. Call once per frame with the same delta the core gets. */
  update(dtMs: number): void {
    const direction = this.activeDirection();
    if (direction === null) return;

    this.dasChargeMs += dtMs;
    if (this.dasChargeMs < TUNING.dasMs) return;

    if (TUNING.arrMs <= 0) {
      // Instant ARR: slam to the wall in one frame.
      for (let i = 0; i < TUNING.board.width; i++) this.dispatch(moveAction(direction));
      return;
    }

    this.arrAccMs += dtMs;
    let budget = TUNING.board.width;
    while (this.arrAccMs >= TUNING.arrMs && budget-- > 0) {
      this.arrAccMs -= TUNING.arrMs;
      this.dispatch(moveAction(direction));
    }
  }

  private activeDirection(): Direction | null {
    return this.held.length > 0 ? this.held[this.held.length - 1] : null;
  }

  /**
   * Feed a keydown in. Public so a host that routes keys between screens can
   * forward events instead of letting this class own the listener.
   */
  keyDown(event: KeyboardEvent): void {
    if (event.repeat) {
      // Repeats are generated by DAS/ARR, not by the OS.
      event.preventDefault();
      return;
    }

    switch (event.code) {
      case 'ArrowLeft':
        event.preventDefault();
        this.pressDirection(-1);
        return;
      case 'ArrowRight':
        event.preventDefault();
        this.pressDirection(1);
        return;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.softDropHeld) {
          this.softDropHeld = true;
          this.dispatch(Action.SoftDropStart);
        }
        return;
      default:
        break;
    }

    const action = KEY_ACTIONS[event.code];
    if (action === undefined) return;
    event.preventDefault();
    this.dispatch(action);
  }

  /** Feed a keyup in. See `keyDown`. */
  keyUp(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowLeft':
        this.releaseDirection(-1);
        return;
      case 'ArrowRight':
        this.releaseDirection(1);
        return;
      case 'ArrowDown':
        if (this.softDropHeld) {
          this.softDropHeld = false;
          this.dispatch(Action.SoftDropEnd);
        }
        return;
      default:
        return;
    }
  }

  private pressDirection(direction: Direction): void {
    this.held = this.held.filter((d) => d !== direction);
    this.held.push(direction);
    // Fire immediately, then charge DAS for the repeat.
    this.dasChargeMs = 0;
    this.arrAccMs = 0;
    this.dispatch(moveAction(direction));
  }

  private releaseDirection(direction: Direction): void {
    const before = this.held.length;
    this.held = this.held.filter((d) => d !== direction);
    if (this.held.length === before) return;
    // Switching to the other held direction restarts the charge.
    this.dasChargeMs = 0;
    this.arrAccMs = 0;
    if (this.held.length > 0) this.dispatch(moveAction(this.activeDirection() as Direction));
  }

  /** Forget every held key, ending any soft drop. */
  releaseAll(): void {
    this.held = [];
    this.dasChargeMs = 0;
    this.arrAccMs = 0;
    if (this.softDropHeld) {
      this.softDropHeld = false;
      this.dispatch(Action.SoftDropEnd);
    }
  }
}

function moveAction(direction: Direction): Action {
  return direction < 0 ? Action.MoveLeft : Action.MoveRight;
}
